// ─────────────────────────────────────────────────────────────────────────────
// Reporting data adapter — the seam for every non-grievance report.
//
//   getDataset(domain, { facilityCcn = "ALL" })
//
// Tries the live PCC feed first (via the serverless proxy) for PCC-backed
// domains; falls back to deterministic mock when PCC is disabled, the domain
// isn't wired yet, or the call fails. Report definitions keep calling
// getDataset(...) unchanged whether the data is live or mock.
// ─────────────────────────────────────────────────────────────────────────────

import { getDomainRows } from "./mockClinical";
import { getPccDataset } from "./pccAdapter";
import { getCmsQualityMeasures } from "./cmsAdapter";
import { getCachedDataset, setCachedDataset, isFreshWindow } from "./datasetCache";

// Quality Measures come from CMS (by CCN), not PointClickCare.
const CMS_DOMAINS = new Set(["qualityMeasures", "qualityMeasuresShort"]);

/**
 * The cached seam. Every report goes through here, and ReportEngine remounts on
 * every report change, so without the cache navigating the tree re-bought each
 * dataset — several of which cost one PointClickCare call per resident on the
 * server. Refresh (which clears the cache) is now the only thing that spends
 * quota, which is what a person pressing a Refresh button already believes.
 *
 * An empty result is cached too. "This facility has no episodes" is an answer,
 * and re-asking for it on every visit costs exactly as much as asking for a
 * full one.
 */
export async function getDataset(domain, opts = {}) {
  const { facilityCcn = "ALL", source } = opts;
  const hit = getCachedDataset(domain, facilityCcn, source);
  if (hit) return hit.rows;
  // Straight after a Refresh, tell the server to skip ITS cache too — otherwise
  // the button clears one copy and is answered from the other.
  const rows = await fetchDataset(domain, { ...opts, fresh: isFreshWindow() });
  setCachedDataset(domain, facilityCcn, source, rows || []);
  return rows;
}

async function fetchDataset(domain, { facilityCcn = "ALL", source, fresh = false } = {}) {
  // CMS-sourced domains: try the CMS relay for the live org; demo org uses mock.
  if (CMS_DOMAINS.has(domain)) {
    if (source === "pcc") {
      const cms = await getCmsQualityMeasures(facilityCcn);
      return cms || []; // empty -> report shows its "comes from CMS" note
    }
    return getDomainRows(domain, facilityCcn);
  }

  const live = await getPccDataset(domain, { facilityCcn, source, fresh });
  if (live && live.length) return live;
  // Live PCC org: NEVER fall back to sample data. Return live-only (empty when
  // PCC doesn't serve the domain); the report surfaces a "why / what's needed"
  // notice instead of fake rows. Only the demo org uses mock.
  if (source === "pcc") return live || [];
  return getDomainRows(domain, facilityCcn);
}

